import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HarnessToolPolicies, PageKey } from '@erp/rpc';
import { orpc, rpcClient } from '@/api/rpc/client';
import { HARNESS_PAGE_SIZE } from './useHarness';

export type ToolCallExecutor = 'server' | 'browser';
export type ToolCallEffect =
  'read' | 'ui' | 'direct' | 'preview' | 'apply' | 'undo';
export type ToolCallStatus =
  | 'running'
  | 'ok'
  | 'failed'
  | 'pending'
  | 'applied'
  | 'discarded'
  | 'expired'
  | 'undone';

export interface ToolCallFilters {
  moduleKey?: string;
  pageKey?: string;
  toolName?: string;
  userId?: string;
  executor?: ToolCallExecutor;
  effect?: ToolCallEffect;
  status?: ToolCallStatus;
  entityId?: string;
  from?: string;
  to?: string;
}

/** Drops blank filter values so the wire request carries only real filters. */
export function compactFilters(filters: ToolCallFilters): ToolCallFilters {
  const out: ToolCallFilters = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

export function useHarnessToolCallSummary(filters: ToolCallFilters) {
  return useQuery({
    ...orpc.harness.toolCalls.summary.queryOptions({
      input: compactFilters(filters),
    }),
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });
}

export function useHarnessToolCalls(
  filters: ToolCallFilters,
  offset: number,
  limit = HARNESS_PAGE_SIZE,
) {
  return useQuery({
    ...orpc.harness.toolCalls.list.queryOptions({
      input: { ...compactFilters(filters), limit, offset },
    }),
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });
}

export function useHarnessToolCall(toolCallId: string | null) {
  return useQuery({
    ...orpc.harness.toolCalls.get.queryOptions({
      input: { toolCallId: toolCallId ?? '' },
    }),
    enabled: !!toolCallId,
  });
}

/** The assistant's calls on one record (the Agent rail list). */
export function useEntityToolCalls(entityId: string | undefined, limit = 20) {
  return useQuery({
    ...orpc.harness.toolCalls.list.queryOptions({
      input: { entityId: entityId ?? '', limit, offset: 0 },
    }),
    enabled: !!entityId,
    staleTime: 15_000,
  });
}

export function useHarnessToolPolicies() {
  return useQuery({
    ...orpc.harness.policies.list.queryOptions({}),
    placeholderData: (previous) => previous,
  });
}

/**
 * Policy mutations return the whole list, so the cache is replaced from the
 * response rather than refetched.
 */
export function useHarnessToolPolicyMutations() {
  const queryClient = useQueryClient();
  const listKey = orpc.harness.policies.list.queryKey({});
  const replaceList = (data: HarnessToolPolicies) => {
    queryClient.setQueryData(listKey, data);
  };
  const updateMode = useMutation({
    mutationFn: rpcClient.harness.policies.update,
    onSuccess: replaceList,
  });
  const updateWriteCap = useMutation({
    mutationFn: rpcClient.harness.policies.updateWriteCap,
    onSuccess: replaceList,
  });
  return { updateMode, updateWriteCap };
}

export function useHarnessRecommendations(
  pageKey: PageKey | undefined,
  entityId: string | undefined,
) {
  return useQuery({
    ...orpc.harness.recommendations.list.queryOptions({
      input: { pageKey: pageKey ?? '', entityId },
    }),
    enabled: !!pageKey,
    staleTime: 60_000,
  });
}
